import TermsOfService from '@components/TermsOfService/TermsOfService';
import { faChevronRight, faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Timeout from 'await-timeout';
import valid from 'card-validator';
import { Form, Formik } from 'formik';
import Cookies from 'js-cookie';
import Image from 'next/future/image';
import Router from 'next/router';
import React, { useEffect, useState } from 'react';
import { ReactSVG } from 'react-svg'
import { simpleStates } from '../../../data/states';
import {
  attemptPayment,
  convertQuoteToInvoice,
  createAccount,
  createQuote,
  createLead,
  updateLead,
  fetchProductDetails,
  sendPaymentReceipt,
  getUserIp,
  getTimezone,
} from '../../../services';
import Input from '../../Inputs/v2/Input';
import Select from '../../Inputs/v2/Select';
import Notice from '../../Notice/Notice';
import EinCheckoutStyles from './EinCheckoutStyles';
import validationSchema from './validationSchema';

const processingOptions = [
  {
    amount: 30,
    label: '24-Hour Processing',
    slug: 'ein-priority-processing',
    value: '24-Hour Processing',
  },
  {
    amount: 0,
    label: '5 Business Day Processing',
    slug: 'ein-standard-processing',
    value: '5 Business Day Processing',
  },
];

const fiveDayProcessing = 99;
const einAssistantFee = 0;

const EINCheckoutComponent = ({ leadValues, phone }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isUnsuccessful, setIsUnsuccessful] = useState(false);
  const [total, setTotal] = useState('99');
  const [processing, setProcessing] = useState(processingOptions[0]);
  const [cardType, setCardType] = useState(null);
  const [cardNumberMask, setCardNumberMask] = useState('9999 9999 9999 9999');
  const [CVVMask, setCVVMask] = useState('999');

  useEffect(() => {
    setProcessing(processingOptions[1]);
    setTotal(einAssistantFee + fiveDayProcessing);
  }, []);

  useEffect(() => {
    if (cardType === 'american-express') {
      setCardNumberMask('9999 999999 99999');
      setCVVMask('9999');
    } else {
      setCardNumberMask('9999 9999 9999 9999');
      setCVVMask('999');
    }
  }, [cardType]);

  return (
    <EinCheckoutStyles>
      <Formik
        initialValues={{
          billing_city: '',
          billing_state: '',
          billing_street_address: '',
          billing_zip_code: '',
          card_number: '',
          cvv: '',
          email: '',
          expiration_date: '',
          first_name: '',
          last_name: '',
          phone: '',
          processing: '5 Business Day Processing',
          sms_opt_in: true,
        }}
        validationSchema={validationSchema}

        onSubmit={async (values, { setSubmitting }) => {
          setSubmitting(true);
          setIsLoading(true);

          const timezoneRes = await getTimezone();

          const errMessage = `Something went wrong. Please give us a call:  1-805-565-1300`;

          const userDetails = {
            CB30Trial: 1,
            first_name: values.first_name,
            last_name: values.last_name,
            email: values.email,
            phone: values.phone,
            Calculated_Timezone__c: timezoneRes,
          };

          // 1. create a lead if one not present in cookies //
          if (!Cookies.get('LeadID')) {
            try {
              const { data: leadResponse } = await createLead(
                userDetails
              );

              const updateuserDetails = {
                SMS_Opt_Out__c: !values.sms_opt_in,
              }
              updateLead(updateuserDetails)

              Cookies.set('LeadID', leadResponse.LeadID);
              Cookies.set('SFLeadID', leadResponse.LeadID);
            } catch (error) {
              setSubmitting(false);
              setIsLoading(false);
              setIsUnsuccessful(errMessage);
              return;
            }
          }

          // used for checkout, and prefill fields on following page
          sessionStorage.setItem('account', JSON.stringify(userDetails));

          // 2. create account //
          try {
            const { data: accountResponse } = await createAccount(
              userDetails,
              leadValues
            );
            Cookies.set('AccountID', accountResponse.AccountID);
          } catch (error) {
            setSubmitting(false);
            setIsLoading(false);
            setIsUnsuccessful(errMessage);
            return;
          }

          // 3. Create a quote //
          const billingAddress = {
            street: values.billing_street_address,
            city: values.billing_city,
            state: values.billing_state,
            zip_code: values.billing_zip_code,
          };

          sessionStorage.setItem('address', JSON.stringify(billingAddress));

          const detailsResponse = await fetchProductDetails([processing.slug]);

          const items = detailsResponse.data.products.map(product => {
            let tier;

            switch (product.slug) {
              case 'ein-standard-processing':
                tier = 'o1';
                break;
              case 'ein-priority-processing':
                tier = 'o3';
                break;
              default:
                break;
            }

            return {
              id: product.id,
              tier,
              quantity: 1,
            };
          });

          let quoteResponse;
          try {
            quoteResponse = await createQuote(billingAddress, items);
          } catch {
            setSubmitting(false);
            setIsLoading(false);
            setIsUnsuccessful(errMessage);
            return;
          }

          // 4. create transaction //
          const expYear = valid.expirationDate(values.expiration_date).year;
          const expMonth = valid.expirationDate(values.expiration_date).month;

          const paymethods = [
            {
              // name: values.name_on_card,
              number: values.card_number,
              expdate_year: expYear,
              expdate_month: expMonth,
              cvv: values.cvv,
              address: values.billing_street_address,
              city: values.billing_city,
              state: values.billing_state,
              postcode: values.billing_zip_code,
            },
          ];

          const maxTries = 10;
          const delayBetweenTries = 2500;
          let paymentIsSuccessful = false;
          let hasResponse = false;
          const userInfo = {};

          try {
            const { data: userIp } = await getUserIp();
            userInfo.ip = userIp;
          } catch (e) {
            console.log(e);
          }

          for (let i = 0; i < maxTries; i += 1) {
            try {
              const { data: paymentResponse } = await attemptPayment(
                quoteResponse.data.data.QuoteList,
                paymethods,
                userInfo
              );
              sessionStorage.setItem(
                'payment',
                JSON.stringify({
                  confirmation: paymentResponse?.response?.id,
                  cc_id: paymentResponse?.details?.creditcard_id,
                  total,
                })
              );

              // if transaction is successful
              if (paymentResponse.details.gateway_status === 1) {
                // Convert quote to invoice
                // await Invoice creation, then send payment reciept
                await convertQuoteToInvoice(quoteResponse.data.data.QuoteList);

                sendPaymentReceipt({
                  id: paymentResponse.response.id,
                  email: values.email,
                });
                hasResponse = true;
                paymentIsSuccessful = true;
                break;
              }
            } catch (error) {
              // if transaction is declined
              if (error?.response?.data?.status === 'Error') {
                setSubmitting(false);
                hasResponse = true;
                setIsLoading(false);
                setIsUnsuccessful(error.response.data.message || errMessage);
                break;
              }
            }
            await Timeout.set(delayBetweenTries);
          }

          if (!paymentIsSuccessful && !hasResponse) {
            setSubmitting(false);
            setIsLoading(false);
            setIsUnsuccessful(errMessage);
            return;
          }

          if (paymentIsSuccessful) {
            Router.push('/services/ein-tax-id-filing/forms');
          }
        }}
      >
        {({ handleChange, handleSubmit, isSubmitting }) => (
          <Form
            onSubmit={handleSubmit}
            className={['content-wrapper', isLoading ? 'is-loading' : ''].join(
              ' '
            )}
          >
            <div className="payment-form-container container">
              <div className="row">
                <div className="col-12">
                  <h1 className="form-title">Order Your EIN Today</h1>
                  <p className="form-subtitle">
                    Fill out the information below and check out to order your
                    EIN/Tax ID.
                  </p>
                  <div className="inputs-wrapper">
                    <p className="section-title">Information</p>
                    <div className="row">
                      <div className="col-12">
                        <Input name="first_name" label="First Name" required />
                      </div>
                      <div className="col-12">
                        <Input name="last_name" label="Last Name" required />
                      </div>
                      <div className="col-12">
                        <Input
                          name="email"
                          label="Email Address"
                          type="email"
                          required
                        />
                      </div>
                      <div className="col-12">
                        <Input
                          name="phone"
                          label="Phone Number"
                          type="tel"
                          maxLength="11"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="inputs-wrapper">
                    <p className="section-title">Billing Address</p>
                    <div className="row">
                      <div className="col-12">
                        <Input
                          name="billing_street_address"
                          label="Street Address"
                          required
                        />
                      </div>
                      <div className="col-12">
                        <Input name="billing_city" label="City" required />
                      </div>
                    </div>
                    <div className="row narrow-gap">
                      <div className="col-6">
                        <Select
                          name="billing_state"
                          label="State"
                          options={simpleStates}
                          required
                          placeholder={true}
                        />
                      </div>
                      <div className="col-6">
                        <Input
                          name="billing_zip_code"
                          label="Zip Code"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="inputs-wrapper">
                    <p className="section-title">Payment Info</p>
                    <div className="row narrow-gap">
                      <div className="col-sm-6 col-12">
                        <Input
                          name="card_number"
                          label="Card Number"
                          mask={cardNumberMask}
                          className={['credit-card-field', cardType].join(' ')}
                          required
                          onChange={e => {
                            handleChange(e);
                            const { value } = e.target;
                            setCardType(
                              valid.number(value)?.card?.type || null
                            );
                          }}
                        />
                      </div>
                      <div className="col-sm-4 col-6">
                        <Input
                          name="expiration_date"
                          label="Exp (MM/YYYY)"
                          mask="99/9999"
                          required
                        />
                      </div>
                      <div className="col-sm-2 col-6">
                        <Input name="cvv" label="CVV" mask={CVVMask} required />
                      </div>
                    </div>
                  </div>
                  <div className="submit-wrapper d-block d-lg-none">
                    <div className="row">
                      <div className="col-12">
                        {isUnsuccessful && (
                          <Notice
                            style={{ textAlign: 'center' }}
                            type="error"
                            message={isUnsuccessful}
                          />
                        )}
                        <button
                          type="submit"
                          className="btn btn-secondary btn-block"
                          disabled={isSubmitting}
                        >
                          <FontAwesomeIcon icon={faLock} size="2x" />
                          Agree and Pay Now
                          <FontAwesomeIcon icon={faChevronRight} size="2x" />
                        </button>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 text-center mt-4">
                        <span className="small">
                          <TermsOfService
                            id="sms_opt_in"
                            buttonText="Agree and Pay Now"
                          />
                        </span>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 cc-wrapper">
                        <Image
                          src="/icons/credit-cards/visa.png"
                          alt=""
                          width={1200}
                          height={600}
                          layout="raw"
                        />
                        <Image
                          src="/icons/credit-cards/mastercard.png"
                          alt=""
                          width={1200}
                          height={600}
                          layout="raw"
                        />
                        <Image
                          src="/icons/credit-cards/amex.png"
                          alt=""
                          width={1200}
                          height={600}
                          layout="raw"
                        />
                        <Image
                          src="/icons/credit-cards/discover.png"
                          alt=""
                          width={1200}
                          height={600}
                          layout="raw"
                        />
                      </div>
                    </div>
                    <div className="row d-flex d-lg-none">
                      <div className="col-12">
                        <div className="ssl-wrapper">
                          <Image
                            src="/static/images/ssl.png"
                            alt=""
                            height={50}
                            width={58}
                          />
                          <span className="small">
                            1-800Accountant uses SSL encryption to protect your
                            payment information.
                          </span>
                        </div>
                        <p className="email-disclaimer text-center">
                          By joining our mailing list, expect to receive new
                          product notifications, the latest news and tax tips
                          conveniently in your inbox!
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="payment-details-container">
              <div className="bg-wrapper">
                <h3 className="details-title">Total Due Today</h3>
                <h1 className="price">${total}</h1>
                <div className="list-item-wrapper container">
                  <div className="row">
                    <p>EIN Filing Fee</p>
                    <div className="price-and-check">
                      <ReactSVG
                        src="/static/images/icons/check.svg?type=svg"
                        beforeInjection={svg => {
                          svg.setAttribute('height', 20);
                        }}
                      />
                      <p>$99</p>
                    </div>
                  </div>
                </div>
                <div className="container">
                  <div className="row did-you-know">
                    <div className="col-12">
                      <div className="d-flex">
                        <img src="/static/images/icons/did-you-know.svg" alt="Did you know" width="500px" height="500px" />
                        <h3>Did You Know?</h3>
                      </div>
                      <p>
                        This business expense is
                        <br />
                        <b>fully tax-deductible.</b>
                      </p>
                    </div>
                  </div>
                  <div className="submit-wrapper d-none d-lg-block">
                    <div className="row">
                      <div className="col-12">
                        {isUnsuccessful && (
                          <Notice type="error" message={isUnsuccessful} />
                        )}
                        <button
                          type="submit"
                          className="btn btn-secondary btn-block"
                          disabled={isSubmitting}
                        >
                          <FontAwesomeIcon icon={faLock} size="2x" />
                          Agree and Pay Now
                          <FontAwesomeIcon icon={faChevronRight} size="2x" />
                        </button>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 text-center mt-4">
                        <span className="small">
                          <TermsOfService
                            id="sms_opt_in"
                            buttonText="Agree and Pay Now"
                          />
                        </span>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 cc-wrapper">
                        <Image
                          src="/icons/credit-cards/visa.png"
                          alt=""
                          width={1200}
                          height={600}
                          layout="raw"
                        />
                        <Image
                          src="/icons/credit-cards/mastercard.png"
                          alt=""
                          width={1200}
                          height={600}
                          layout="raw"
                        />
                        <Image
                          src="/icons/credit-cards/amex.png"
                          alt=""
                          width={1200}
                          height={600}
                          layout="raw"
                        />
                        <Image
                          src="/icons/credit-cards/discover.png"
                          alt=""
                          width={1200}
                          height={600}
                          layout="raw"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row d-none d-lg-flex">
                    <div className="col-12">
                      <div className="ssl-wrapper">
                        <Image
                          src="/static/images/ssl.png"
                          alt=""
                          height={50}
                          width={58}
                        />
                        <span className="small">
                          1-800Accountant uses SSL encryption to protect your
                          payment information.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <p className="email-disclaimer text-center d-none d-lg-block">
                By joining our mailing list, expect to receive new product
                notifications, the latest news and tax tips conveniently in your
                inbox!
              </p>
            </div>
          </Form>
        )}
      </Formik>
    </EinCheckoutStyles >
  );
};

export default EINCheckoutComponent;
